import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import axios, { API, authHeaders, formatError } from "../../lib/api";

const MATERIALS = [["rice_husk", "Rice husk"], ["paddy_straw", "Paddy straw"], ["wheat_bhusa", "Wheat bhusa"], ["mustard_stalk", "Mustard stalk"]];
const CONTACT_FIELDS = [["address", "Plant address"], ["map_query", "Map search (place name or lat,long)"], ["phone", "Phone"], ["whatsapp", "WhatsApp number (+91…)"], ["email", "Email"]];
const card = "rounded-[24px] border hairline bg-[var(--forest-2)] p-6 sm:p-8";

const RatesForm = ({ rates, onSaved }) => {
  const [vals, setVals] = useState(() => Object.fromEntries(MATERIALS.map(([k]) => [k, rates?.rates?.[k] ?? ""])));
  const [note, setNote] = useState(rates?.note ?? "");
  const save = async (e) => {
    e.preventDefault();
    const body = { rates: Object.fromEntries(MATERIALS.map(([k]) => [k, vals[k] === "" ? null : parseInt(vals[k], 10)])), note };
    try {
      await axios.put(`${API}/admin/rates`, body, { headers: authHeaders() });
      toast.success("Buying rates updated live");
      onSaved();
    } catch (err) {
      toast.error(formatError(err));
    }
  };
  return (
    <form data-testid="admin-rates-form" onSubmit={save} className={card}>
      <p className="font-display text-2xl font-semibold">This week's buying rates</p>
      <p className="text-sm text-[var(--sage)]">₹ per tonne paid to farmers. Leave blank to show "Rate on call".</p>
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        {MATERIALS.map(([k, label]) => (
          <div key={k} className="space-y-1.5">
            <Label htmlFor={`r-${k}`}>{label}</Label>
            <Input id={`r-${k}`} data-testid={`admin-rate-input-${k}`} type="number" min="0" max="100000" value={vals[k]} onChange={(e) => setVals({ ...vals, [k]: e.target.value })} className="h-12" />
          </div>
        ))}
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="r-note">Note for farmers (optional, shown as typed)</Label>
          <Input id="r-note" data-testid="admin-rate-note-input" maxLength={200} value={note} onChange={(e) => setNote(e.target.value)} className="h-12" />
        </div>
      </div>
      <button data-testid="admin-rates-save" className="mt-6 w-full h-12 rounded-full bg-[var(--husk)] text-[var(--forest)] font-semibold">Save rates</button>
    </form>
  );
};

const ContactForm = () => {
  const [c, setC] = useState(null);
  useEffect(() => {
    axios.get(`${API}/contact`).then((r) => setC(r.data)).catch((e) => toast.error(formatError(e)));
  }, []);
  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.put(`${API}/admin/contact`, c, { headers: authHeaders() });
      setC(data);
      toast.success("Contact page updated");
    } catch (err) {
      toast.error(formatError(err));
    }
  };
  if (!c) return null;
  return (
    <form data-testid="admin-contact-form" onSubmit={save} className={card}>
      <p className="font-display text-2xl font-semibold">Contact page</p>
      <p className="text-sm text-[var(--sage)]">Empty fields show "Coming soon" on the site.</p>
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        {CONTACT_FIELDS.map(([k, label]) => (
          <div key={k} className={`space-y-1.5 ${k === "address" || k === "map_query" ? "sm:col-span-2" : ""}`}>
            <Label htmlFor={`c-${k}`}>{label}</Label>
            <Input id={`c-${k}`} data-testid={`admin-contact-input-${k}`} value={c[k] ?? ""} onChange={(e) => setC({ ...c, [k]: e.target.value })} className="h-12" />
          </div>
        ))}
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="c-hours">Opening hours (one line per day range)</Label>
          <Textarea id="c-hours" data-testid="admin-contact-input-hours" rows={3} value={c.hours ?? ""} onChange={(e) => setC({ ...c, hours: e.target.value })} />
        </div>
      </div>
      <button data-testid="admin-contact-save" className="mt-6 w-full h-12 rounded-full bg-[var(--husk)] text-[var(--forest)] font-semibold">Save contact page</button>
    </form>
  );
};

export const RatesContact = ({ rates, onSaved }) => (
  <div className="grid lg:grid-cols-2 gap-5 items-start">
    {rates ? <RatesForm rates={rates} onSaved={onSaved} /> : <div />}
    <ContactForm />
  </div>
);
